import { EVENT_TYPE_KEYS } from 'constants/eventTypes.constants';
import { ParcelEvent } from 'interfaces/ParcelEvent';
import { MixedEvent } from '@pperform/pp-data-mappers';

/**
 * Finds the index of the G32 (Arrived at parcel store) event in an events array
 * Since events are ordered with newest first (index 0) and oldest last,
 * we find the last occurrence of G32 (first encounter when scanning backwards from end to start)
 */
export const findG32EventIndex = (
  events?: Array<{ eventTypeMasterData?: { key: string } }>,
): number => {
  if (!events?.length) return -1;

  // Scan from the end backwards to find the first G32 encountered (oldest G32 event)
  for (let i = events.length - 1; i >= 0; i -= 1) {
    if (
      events[i]?.eventTypeMasterData?.key ===
      EVENT_TYPE_KEYS.ARRIVED_AT_PARCEL_STORE
    ) {
      return i;
    }
  }

  return -1;
};

/**
 * Replaces event locations with pickup_point value for events from latest back to and including G32
 * Handles for ParcelEvent
 * Only replaces if the event already has a truthy location or eventLocation value
 * @param events - Array of parcel events to process
 * @param pickupPoint - The pickup point value to use for location replacement
 * @returns New events array with locations replaced (only where existing location/eventLocation is truthy)
 */
export const applyPickupLocationToParcelEvents = (
  events: ParcelEvent[] | undefined,
  pickupPoint: string,
): ParcelEvent[] | undefined => {
  if (!events?.length || !pickupPoint) {
    return events;
  }

  const g32Index = findG32EventIndex(events);

  // If G32 event not found, return events unchanged
  if (g32Index === -1) {
    return events;
  }

  // Replace location for events from latest (index 0) back to G32 event (include)
  // Only replace if the event already has a truthy location or eventLocation value
  return events.map((event, index) => {
    if (index <= g32Index && (event.location || event.eventLocation)) {
      return {
        ...event,
        location: pickupPoint, // shipment.events is using location (deprecated alias)
        eventLocation: pickupPoint, // Primary location field
      };
    }
    return event;
  });
};

/**
 * Replaces event locations with pickup_point value for events from latest back to and including G32
 * Handles for MixedEvent
 * Only replaces if the event already has a truthy eventLocation value
 * @param events - Array of mixed events to process
 * @param pickupPoint - The pickup point value to use for location replacement
 * @returns New events array with locations replaced (only where existing eventLocation is truthy)
 */
export const applyPickupLocationToMixedEvents = (
  events: MixedEvent[] | undefined,
  pickupPoint: string,
): MixedEvent[] | undefined => {
  if (!events?.length || !pickupPoint) {
    return events;
  }

  const g32Index = findG32EventIndex(events);

  // If G32 event not found, return events unchanged
  if (g32Index === -1) {
    return events;
  }

  // Replace location for events from latest (index 0) back to G32 event (include)
  // Only replace if the event already has a truthy eventLocation value
  return events.map((event, index) => {
    if (index <= g32Index && event.eventLocation) {
      // MixedEvent only uses `eventLocation`, NO `location`
      return MixedEvent.create({ ...event, eventLocation: pickupPoint });
    }
    return event;
  });
};
